import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrgContext = createContext(null);

export const OrgProvider = ({ children }) => {
  const [organization, setOrganization] = useState(null);
  const [isOrgRoute, setIsOrgRoute] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orgSlug, setOrgSlug] = useState(null);

  // Check if current path is an org route
  useEffect(() => {
    const checkOrgRoute = async () => {
      const path = window.location.pathname;
      const pathParts = path.split('/').filter(Boolean);
      
      // Check if first part could be an org slug (not a known route)
      const knownRoutes = ['gst', 'login', 'register', 'dashboard', 'api'];
      
      if (pathParts.length > 0 && !knownRoutes.includes(pathParts[0])) {
        const potentialSlug = pathParts[0];
        
        try {
          const response = await fetch(`${API_URL}/api/org/by-slug/${potentialSlug}`);
          if (response.ok) {
            const orgData = await response.json();
            setOrganization(orgData);
            setIsOrgRoute(true);
            setOrgSlug(potentialSlug);
          } else {
            setIsOrgRoute(false);
            setOrganization(null);
            setOrgSlug(null);
          }
        } catch (error) {
          console.error('Error fetching org:', error);
          setIsOrgRoute(false);
        }
      } else {
        setIsOrgRoute(false);
        setOrganization(null);
        setOrgSlug(null);
      }
      
      setLoading(false);
    };

    checkOrgRoute();
  }, []);

  // Check if email has access to current org
  const checkOrgAccess = async (email) => {
    if (!orgSlug) return { has_access: false, reason: 'no_org' };
    
    try {
      const response = await fetch(
        `${API_URL}/api/org/${orgSlug}/check-access?email=${encodeURIComponent(email)}`
      );
      return await response.json();
    } catch (error) {
      console.error('Error checking org access:', error);
      return { has_access: false, reason: 'error' };
    }
  };

  // Org-specific login
  const orgLogin = async (email, password) => {
    if (!orgSlug) {
      return { success: false, error: 'Not on an organization page' };
    }

    try {
      const response = await fetch(`${API_URL}/api/org/${orgSlug}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.detail || 'Login failed' };
      }

      const data = await response.json();
      
      // Store token and org info
      localStorage.setItem('taxsetu_token', data.access_token);
      localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
      localStorage.setItem('taxsetu_org', JSON.stringify(data.organization));
      
      return { success: true, data };
    } catch (error) {
      console.error('Org login error:', error);
      return { success: false, error: 'Connection error' };
    }
  };

  // Set org manually (for navigation)
  const setOrgBySlug = async (slug) => {
    try {
      const response = await fetch(`${API_URL}/api/org/by-slug/${slug}`);
      if (response.ok) {
        const orgData = await response.json();
        setOrganization(orgData);
        setIsOrgRoute(true);
        setOrgSlug(slug);
        return orgData;
      }
    } catch (error) {
      console.error('Error setting org:', error);
    }
    return null;
  };

  // Clear org context
  const clearOrg = () => {
    setOrganization(null);
    setIsOrgRoute(false);
    setOrgSlug(null);
    localStorage.removeItem('taxsetu_org');
  };

  return (
    <OrgContext.Provider value={{
      organization,
      isOrgRoute,
      orgSlug,
      loading,
      checkOrgAccess,
      orgLogin,
      setOrgBySlug,
      clearOrg
    }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};
